import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useRef, useState } from 'react';
const userPrefersDarkTheme = window.matchMedia('(prefers-color-scheme: dark)').matches;
const THEMES = ['dark', 'light'];
function getFromLocalStorage(key) {
    const value = window.localStorage.getItem(key);
    if (typeof value === 'string' && THEMES.includes(value)) {
        return value;
    }
}
function assertDefaultTheme(localStorageKey, options) {
    const localStorageValue = getFromLocalStorage(localStorageKey);
    // Start with default theme, which itself defaults to 'light'
    let defaultTheme = options?.defaultTheme ?? 'light';
    // Override it with the user's preferred theme (OS setting)
    if (options?.useOsPreference !== false) {
        defaultTheme = userPrefersDarkTheme ? 'dark' : 'light';
    }
    // Unless we have a localStorage value, this always takes precedence
    if (options?.rememberThemeSetting !== false && localStorageValue) {
        defaultTheme = localStorageValue;
    }
    return defaultTheme;
}
export const WuiThemeContext = createContext(null);
export function WuiThemeProvider({ children, options }) {
    const localStorageKey = typeof options?.rememberThemeSetting === 'string' ? options.rememberThemeSetting : 'wui-theme';
    const [theme, setTheme] = useState(assertDefaultTheme(localStorageKey, options));
    const themeSetter = useRef((theme) => {
        setTheme(theme);
        if (options?.rememberThemeSetting !== false) {
            window.localStorage.setItem(localStorageKey, theme);
        }
    }).current;
    useEffect(() => {
        if (options?.applyBodyClass !== false) {
            document.body.setAttribute('theme', theme);
        }
        for (const [t, classes] of Object.entries(options?.applyBodyClasses ?? {})) {
            for (const c of classes) {
                document.body.classList.toggle(c, t === theme);
            }
        }
    }, [theme, options]);
    return (_jsx(WuiThemeContext.Provider, { value: {
            theme,
            setTheme: themeSetter,
        }, children: children }));
}
export function useWuiTheme() {
    const ctx = useContext(WuiThemeContext);
    if (ctx === null) {
        throw new Error('<WuiThemeProvider /> not initialized!');
    }
    return ctx;
}
